/* eslint-disable vue/one-component-per-file */
/**
 * Application
 */

import { directives } from '~/framework/directives';
import { plugins } from '~/framework/plugins';
import { domLoaded } from '~/framework/helpers/dom-ready';
import { loadScript } from '~/framework/network/script-loader';
import { App as VueApp, ComponentPublicInstance, createApp } from '~/framework/vue';

if (process.env.NODE_ENV !== 'test') {
	// Static asset manifest output workaround: https://github.com/vitejs/vite/issues/2375#issuecomment-908579361
	// Until such time as that issue is resolved. (Yes the console.Debug is needed)
	const sprites = import.meta.glob('./assets/svg/*.svg');
	console.debug('[DXA] Sprites:', sprites);

	const graphics = import.meta.glob('/src/assets/graphics/*.svg');
	console.debug('[DXA] Graphics:', graphics);
}

export const IS_READY = 'is-ready';

// Declarations
declare global {
	type AppHookFunction = string | (() => unknown | Promise<unknown>);

	type AppHook = AppHookFunction | AppHookFunction[];

	type AppGlobals = {
		[s: string]: unknown;
		polyfillsLoaded?: boolean;
	};

	type CdnScript = {
		global: string;
		src: string;
	};

	interface AppHooks {
		/**
		 * Executes before auto-start invocation occurs.
		 */
		beforeAutoStart?: AppHook[];

		/**
		 * Executes before any initialisation occurs.
		 */
		initialise?: AppHook[];

		/**
		 * Executes before the App framework (Vue) is mounted.
		 */
		beforeMount?: AppHook[];

		/**
		 * Executes after the App framework (Vue) has mounted.
		 */
		afterMount?: AppHook[];

		/**
		 * Executes at the end of the initialisation process.
		 */
		ready?: AppHook[];

		/**
		 * Executes before the App framework (Vue) is unmounted.
		 */
		beforeUnmount?: AppHook[];

		/**
		 * Executes after the App framework (Vue) is unmounted.
		 */
		afterUnmount?: AppHook[];
	}

	interface Window {
		/**
		 * Provides access to global App class. It is not available until _after_ the application
		 * has initialised (but before `ready` hook).
		 */
		App: App;

		AppFlag: string;

		/**
		 * Generic object facilitating namespaced globals.
		 * Preference is to not require the use
		 * of globals; this object is intended as a last resort.
		 */
		AppGlobals: AppGlobals;

		/**
		 * Array of paths to script fallbacks
		 */
		CdnFallbacks: CdnScript[];

		/**
		 * Application initialisation lifecycle hooks object.
		 */
		AppHooks: AppHooks;
	}
}

// Config
export type AppLogger = Pick<Console, 'log' | 'debug' | 'info' | 'warn'>;

export interface AppConstructorOptions {
	/** Provide a custom logger. Defaults to `console`. */
	logger?: AppLogger;
}

/**
 * Application Wrapper
 */
export class App {
	/**
	 * Runs provided AppHooks
	 */
	private static async runHooks(hooks: AppHook[]): Promise<void> {
		await Promise.all(
			hooks.map(async (hook) => {
				// Hooks in an array should run sequentially
				if (Array.isArray(hook)) {
					//hook.forEach(App.runHook);

					// eslint-disable-next-line unicorn/no-array-for-each,unicorn/no-array-callback-reference
					hook.forEach(App.runHook);
					return;
				}

				return App.runHook(hook);
			})
		);
	}

	private static async runHook(hook: AppHookFunction): Promise<unknown | Error> {
		if (typeof hook === 'string') {
			if (!hook.startsWith('https://') && !hook.startsWith('http://') && !hook.startsWith('/')) {
				throw new Error(`Hook unable to load file at path: ${hook}`);
			}

			return loadScript(hook);
		}

		if (typeof hook !== 'function') {
			// This is an unexpected case which shouldn't happen, so TS may miss it.
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			throw new TypeError(`Hook is not a function:\n${hook}`);
		}

		return hook();
	}

	/** Use `logger` instead of `console` to allow tests to ignore output. */
	public readonly logger: AppLogger;
	private isMounted = false;
	private readonly destroyers: Array<() => void> = [];
	private beforeMountPromiseResolver!: () => void;
	private afterMountPromiseResolver!: () => void;
	private readyPromiseResolver!: () => void;

	private _beforeMount!: Promise<void>;
	public get beforeMount() {
		return this._beforeMount;
	}

	private _afterMount!: Promise<void>;
	public get afterMount() {
		return this._afterMount;
	}

	private _ready!: Promise<void>;
	public get ready() {
		return this._ready;
	}

	private _vueApp: VueApp;
	public get vueApp() {
		return this._vueApp;
	}

	private _vueInstance: ComponentPublicInstance | undefined = undefined;
	public get vueInstance() {
		return this._vueInstance;
	}

	private _status: 'stopped' | 'teardown' | 'starting' | 'running' | 'pending' = 'stopped';
	public get status() {
		return this._status;
	}

	constructor(flag = '', { logger }: AppConstructorOptions = {}) {
		// eslint-disable-next-line vue/one-component-per-file
		this._vueApp = createApp({}); // Satisfy compiler
		this.resetPromises();

		// Logger - defaults to window.console
		this.logger = logger ?? console;

		window.App = this;
		window.AppFlag = flag;
	}

	/**
	 * Automatically starts the Application on load.
	 */
	public async autoStart(): Promise<App> {
		this._status = 'pending';

		if (window.AppHooks.beforeAutoStart) {
			await App.runHooks(window.AppHooks.beforeAutoStart);
		}

		await domLoaded;

		return this.start();
	}

	/**
	 * Starts the Application
	 */
	public async start(): Promise<App> {
		// Ensure only a single instance is running
		if (window.App?.status === 'running' || window.App?.status === 'teardown') {
			return window.App;
		}

		return new Promise<App>((resolve, reject) => {
			this.init()
				.then(() => {
					resolve(this);
					dispatchEvent(new CustomEvent('app.start'));
				})
				.catch(reject);
		});
	}

	/**
	 * Initiate the stop procedure.
	 */
	public async stop(): Promise<void> {
		if (this.status === 'stopped' || this.status === 'teardown') return;
		this.resetPromises();

		await this.teardown();
		document.dispatchEvent(new CustomEvent('app.stop'));
	}

	/**
	 * Restart the application.
	 */
	public async restart(): Promise<void> {
		if (this.status === 'pending') return;
		this.logger.debug('[DXA] App restarting');
		await this.stop();
		await this.start();
	}

	/**
	 * Initialises the application, executing AppHooks
	 */
	private async init(): Promise<void> {
		if (this.status === 'running') return;
		this._status = 'starting';

		if (window.AppHooks.initialise) {
			await App.runHooks(window.AppHooks.initialise);
			if (this.status !== 'starting') return; // In case state changes during async
		}

		this.logger.debug('[DXA] App starting');

		this.initialiseVue();
		directives(this.vueApp);
		plugins(this.vueApp);

		// Components
		// eslint-disable-next-line unicorn/prefer-ternary
		if (window.AppFlag === '' || window.AppFlag === 'all') {
			await import('~/components/components').then((x) => {
				const destroyComponents = x.initialiseComponentsBs(this);
				this.destroyers.push(destroyComponents);
			});
		}

		if (window.AppFlag === 'icq' || window.AppFlag === 'all') {
			await import('~/components/components-icq').then((x) => {
				const destroyComponents = x.initialiseComponentsIcq(this);
				this.destroyers.push(destroyComponents);
			});
		}

		this.beforeMountPromiseResolver();

		if (window.AppHooks.beforeMount) {
			await App.runHooks(window.AppHooks.beforeMount);
			if (this.status !== 'starting') return; // In case state changes during async
		}

		document.dispatchEvent(new CustomEvent('app.beforeMount'));

		// Application
		this.mount();

		this.afterMountPromiseResolver();

		if (window.AppHooks.afterMount) {
			await App.runHooks(window.AppHooks.afterMount);
			if (this.status !== 'starting') return; // In case state changes during async
		}

		document.dispatchEvent(new CustomEvent('app.afterMount'));

		this._status = 'running';

		// Add class after Vue has rendered (some tests watch for this)
		await this.vueInstance?.$nextTick();
		document.documentElement.classList.add(IS_READY);

		this.logger.log('[DXA] App ready');

		this.readyPromiseResolver();

		if (window.AppHooks.ready) {
			await App.runHooks(window.AppHooks.ready);
			// @ts-expect-error Because TS for some reason hasn't realised this is async
			if (this.status !== 'running') return; // In case state changes during async
		}

		document.dispatchEvent(new CustomEvent('app.ready'));
	}

	/**
	 * Tear down the application from the DOM.
	 * @private
	 */
	private async teardown(): Promise<void> {
		this.logger.debug('[DXA] App unmounting');
		this._status = 'teardown';

		while (this.destroyers.length > 0) {
			const destroyer = this.destroyers.pop();
			destroyer?.();
		}

		if (window.AppHooks.beforeUnmount) {
			await App.runHooks(window.AppHooks.beforeUnmount);
			if (this.status !== 'teardown') return; // In case state changes during async
		}

		document.dispatchEvent(new CustomEvent('app.beforeUnmount'));

		// Application
		this.unmount();

		if (window.AppHooks.afterUnmount) {
			await App.runHooks(window.AppHooks.afterUnmount);
			if (this.status !== 'teardown') return; // In case state changes during async
		}

		document.dispatchEvent(new CustomEvent('app.afterUnmount'));

		// Remove class (some tests watch for this)
		document.documentElement.classList.remove(IS_READY);

		this._status = 'stopped';
		this.logger.log('[DXA] App stopped');
	}

	private initialiseVue() {
		// DEV mode global state for rendering comps in isolation
		// Will be removed from production built by webpack
		let initialState = {};
		if (process.env.NODE_ENV !== 'production') {
			initialState = {
				demoState: {},
				demoNum: 1,
				demoStr: '',
			};
		}

		// Global Vue App
		this._vueApp = createApp({
			data() {
				return initialState;
			},
		});
	}

	/**
	 * Mounts the App framework to the DOM
	 */
	private mount(selector = '#app') {
		this._vueInstance = this.vueApp.mount(selector);
		this.isMounted = true;
	}

	/**
	 * Unmount the App Framework from the DOM
	 */
	private unmount() {
		if (!this.isMounted) return;
		this.vueApp.unmount();
		this._vueInstance = undefined;
		this.isMounted = false;
	}

	/**
	 * Resets mounting promises.
	 */
	private resetPromises() {
		this._beforeMount = new Promise((resolve) => {
			this.beforeMountPromiseResolver = resolve;
		});
		this._afterMount = new Promise((resolve) => {
			this.afterMountPromiseResolver = resolve;
		});
		this._ready = new Promise((resolve) => {
			this.readyPromiseResolver = resolve;
		});
	}
}
